import { Group } from '@mantine/core';
import { IconDownload, IconFileUpload, IconSquareArrowRight, IconTrash } from '@tabler/icons-react';
import { useContextMenu } from 'mantine-contextmenu';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import type { SerializableRenderTreeNode } from '../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { sortBy } from '../../../../../react-commons/utils/utilFunctions';
import { SplitPane } from '../../../../../react-commons/views/split-pane/SplitPane';
import { setImportingOrExportingState, setTaskRefresh } from '../../../persisted/global/globalActions';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    defaultProjectResource,
    type ProjectResource,
    useProjectActions,
} from '../../../resource/resources/artifacts/project/projectResource';
import { useUserActions } from '../../../resource/resources/persistables/user/userResource';
import { loadFilesOnline, startDownloadPVTOfLatestVersion } from '../../../resource/services/system/systemActions';
import { TOOLTIPS } from '../../../resource/tooltipTexts';
import { useThunkDispatch } from '../../../store/rootReducer';
import { useUserId } from '../../authentication/useUserId';
import { showUnsavedDataAlert } from '../../hooks/useShowUnsavedDataAlert';
import { ImportProjectDialog } from '../../reusable-components/dialogs/ImportProjectDialog';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { NothingSelectedComponent } from '../../reusable-components/NothingSelectedComponent';
import { useMyNavigate } from '../../router/createdRouter';
import { defaultLoadingNode } from '../../util/parseTreeFunctions';
import { selectIsDirty } from '../../util/useSelectorFunctions';
import { handleMutationResult } from '../../util/utilFunctions';
import { ProjectsComponentSelectedOverview } from './ProjectsComponentSelectedOverview';

type ProjectsComponentTreeNode = SerializableRenderTreeNode<ProjectResource>;

const DownloadHoverButton = ({ project }: { project: ProjectResource }) => {
    const userId = useUserId('');
    const userActions = useUserActions();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, project.id);

    const dispatch = useDispatch();
    const thunkDispatch = useThunkDispatch();

    if (!userSystemRights.data?.isProcessAdmin) {
        return undefined;
    }

    return (
        <TreeHoverButton
            Icon={IconDownload}
            onClick={() =>
                OpenModal.AlertModal(
                    'PVT File Download',
                    'Do you want to download latest Project Version as a PVT File?',
                    () => {
                        dispatch(setImportingOrExportingState(true));
                        void thunkDispatch(startDownloadPVTOfLatestVersion(project.name, project.id)).then((value) => {
                            if (value) {
                                dispatch(setTaskRefresh(true));
                            }
                            dispatch(setImportingOrExportingState(false));
                        });
                    }
                )
            }
            tooltip={{ text: TOOLTIPS.DOWNLOAD }}
        />
    );
};

const parseSystem = (projectOutlines: Array<ProjectResource> | null | undefined): Array<ProjectsComponentTreeNode> => {
    return (
        projectOutlines?.toSorted(sortBy(({ name }) => name)).map((project) => ({
            key: project.id,
            label: project.name,
            data: project,
        })) ?? [defaultLoadingNode]
    );
};

export const ProjectsComponent = () => {
    const dispatch = useDispatch();
    const nestedDialog = useNestedDialogFunction();
    const thunkDispatch = useThunkDispatch();
    const navigate = useMyNavigate();
    const isDirty = useSelector(selectIsDirty);
    const [search, setSearch] = useSearchParams();
    const { showContextMenu } = useContextMenu();
    const userId = useUserId('');
    const projectActions = useProjectActions();
    const userActions = useUserActions();
    const projectsQuery = projectActions.useGetAllResources();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(
        userId,
        projectsQuery.data?.length === 1 ? projectsQuery.data[0].id : undefined
    );
    const addProject = projectActions.useAddResource();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const deleteProjectMutation = projectActions.useDeleteResource();

    const loadProject = useCallback(
        (projectId: string) => {
            navigate({
                route: '/project/:id',
                routeParams: { id: projectId },
            });
        },
        [navigate]
    );

    const deleteProject = useCallback(
        (id: string, name: string) => {
            deleteResourceDialogFunction.deleteResource(id, name, ResourceType.PROJECT, deleteProjectMutation, () =>
                setSearch((prevParams) => {
                    prevParams.delete('selected');
                    return prevParams;
                })
            );
        },
        [deleteProjectMutation, deleteResourceDialogFunction, setSearch]
    );

    const nodes = useMemo(() => parseSystem(projectsQuery.data), [projectsQuery.data]);

    const selectedId = search.get('selected');

    const openImportProjectDialog = () => {
        nestedDialog.showNestedDialog(
            <ImportProjectDialog
                onClose={nestedDialog.closeNestedDialog}
                onSubmit={(name, selectedFiles, errorLevel) => {
                    nestedDialog.closeNestedDialog();

                    void thunkDispatch(loadFilesOnline(selectedFiles, name, errorLevel)).then((loaded) => {
                        if (loaded) {
                            dispatch(setTaskRefresh(true));
                        }
                    });
                }}
            />
        );
    };

    const onNodeClick = useCallback(
        (node: ProjectsComponentTreeNode) => {
            const action = () => {
                setSearch((prevParams) => {
                    prevParams.set('selected', node.key);
                    return prevParams;
                });
            };
            if (isDirty) {
                showUnsavedDataAlert(action);
            } else {
                action();
            }
        },
        [isDirty, setSearch]
    );

    useEffect(() => {
        if (!selectedId) {
            const toSelect = nodes.at(0);
            if (toSelect && toSelect.key !== 'LoadingTree') {
                setSearch((prevParams) => {
                    prevParams.set('selected', toSelect.key);
                    return prevParams;
                });
            }
        }
    }, [nodes, selectedId, setSearch]);

    const isAdmin = Boolean(userSystemRights.data?.isAdmin);

    return (
        <div className={'main-window-content'}>
            <SplitPane secondaryInitialSize={70}>
                <Tree
                    nodes={nodes}
                    filtered
                    selectedNodeKey={selectedId ?? undefined}
                    onNodeSelected={onNodeClick}
                    onContextMenu={(selection) =>
                        showContextMenu([
                            {
                                key: 'project',
                                icon: <IconSquareArrowRight />,
                                title: 'Load Project',
                                onClick: () => loadProject(selection.node.key),
                            },
                        ])(selection.event)
                    }
                    onEllipsisClick={
                        userSystemRights.data?.isAdmin ?
                            showContextMenu([
                                {
                                    key: 'import_project',
                                    title: 'Import Project',
                                    icon: <IconFileUpload />,
                                    onClick: () => openImportProjectDialog(),
                                },
                            ])
                        :   undefined
                    }
                    onAddClick={
                        userSystemRights.data?.isAdmin ?
                            () =>
                                OpenModal.StringInputModal('Create a new Project', (value) => {
                                    addProject.mutate(
                                        {
                                            ...defaultProjectResource,
                                            name: value,
                                        },
                                        {
                                            onSuccess: handleMutationResult((result) => {
                                                setSearch((prevParams) => {
                                                    const id = result.content?.id;
                                                    if (id) {
                                                        prevParams.set('selected', id);
                                                    }
                                                    return prevParams;
                                                });
                                            }),
                                        }
                                    );
                                })
                        :   undefined
                    }
                    getElements={(node) => ({
                        onDoubleClick: () => loadProject(node.data.id),
                        hoverNode: (
                            <Group gap={0}>
                                <Show when={isAdmin}>
                                    <TreeHoverButton
                                        Icon={IconTrash}
                                        onClick={() => deleteProject(node.data.id, node.data.name)}
                                        tooltip={{ text: TOOLTIPS.DELETE_PROJECT }}
                                    />
                                </Show>
                                <DownloadHoverButton project={node.data} />
                                <TreeHoverButton
                                    Icon={IconSquareArrowRight}
                                    onClick={() => loadProject(node.data.id)}
                                    tooltip={{ text: TOOLTIPS.LOAD_PROJECT }}
                                />
                            </Group>
                        ),
                    })}
                />
                <div
                    style={{
                        display: 'inline-block',
                        verticalAlign: 'top',
                        overflowY: 'auto',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Show when={selectedId} fallback={<NothingSelectedComponent />}>
                        {(projectId) => (
                            <ProjectsComponentSelectedOverview
                                project={projectsQuery.data?.find((value) => value.id === projectId)}
                            />
                        )}
                    </Show>
                </div>
            </SplitPane>
        </div>
    );
};
